/**
 * Route loader helpers that fetch the admin (Pages CMS) SEO override for a
 * page alongside the site settings a route already needs.
 *
 * Safe in public route loaders: `getPageSeo` is an unauthenticated read behind
 * the "Anyone can read pages" policy, so SSR and prerender work.
 */
import { getPageSeo } from "./pages.functions";
import { getSiteSettings, type SiteSettings } from "./site-settings.functions";
import type { PageSeoOverride } from "./seo-overrides";

/** Strip a leading slash so route SLUG constants map to `pages.slug`. */
function toDbSlug(slug: string): string {
  const s = slug.replace(/^\/+/, "").replace(/\/+$/, "");
  return s === "" ? "home" : s;
}

export async function loadPageSeo(slug: string): Promise<PageSeoOverride | null> {
  try {
    return await getPageSeo({ data: { slug: toDbSlug(slug) } });
  } catch {
    return null;
  }
}

export type SettingsWithSeo = SiteSettings & { seo: PageSeoOverride | null };

/** Site settings (rating/review count) + the page's SEO override. */
export async function loadPageSettings(slug: string): Promise<SettingsWithSeo> {
  const [settings, seo] = await Promise.all([getSiteSettings(), loadPageSeo(slug)]);
  return { ...settings, seo };
}
